/**
 * AccountsHub — Full accounts overview with letter-grouped list view (Stitch design).
 * Replaces both old AccountsHub and KlantenList.
 */
import { useMsal } from '@azure/msal-react';
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Typography, TextField, InputAdornment, Skeleton, Chip, Tooltip, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';
import { useUserRole } from '../../contexts/UserRoleContext';
import NieuwAccountDialog from './NieuwAccountDialog';
import LogoMetTerugval from '../../ui-components/LogoMetTerugval';

const T = {
  primary: '#526075', tertiary: '#635983', tertiaryContainer: '#dbcdff',
  primaryContainer: '#d5e3fd', bg: '#f8f9fa', surface: '#ffffff',
  surfaceLow: '#f1f4f5', text: '#2d3335', secondary: '#556071',
  outline: '#767c7e', outlineVariant: '#adb3b5', emerald: '#10b981',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const SECTOR_COLORS = {
  'Key account': { color: '#f59e0b', bg: '#fef3c7' },
  'Premium account': { color: '#3b82f6', bg: '#dbeafe' },
  'Normaal account': { color: T.outline, bg: T.surfaceLow },
  'B2B': { color: '#3b82f6', bg: '#dbeafe' },
  'Semi-public': { color: T.tertiary, bg: T.tertiaryContainer },
  'Public': { color: T.emerald, bg: '#ecfdf5' },
  'Agency': { color: '#f59e0b', bg: '#fef3c7' },
  'Only': { color: '#7c3aed', bg: '#ede9fe' },
  'Other': { color: T.outline, bg: T.surfaceLow },
};

function AccountCard({ klant, onClick }) {
  const naam = klant.fields?.Account || 'Onbekend';
  const sector = klant.fields?.Sector || '';
  const status = klant.fields?.Status || '';
  const beeldmerk = klant.fields?.Beeldmerk;
  const logoUrl = Array.isArray(beeldmerk) && beeldmerk.length > 0
    ? beeldmerk[0]?.thumbnails?.small?.url || beeldmerk[0]?.url
    : null;
  const bedrijvenNamen = klant.fields?.BedrijvenNamen || [];
  const aantalProjecten = klant.fields?.['Aantal projecten Rollup (from Bedrijven)'] || klant.fields?.['Aantal projecten'] || 0;
  const sectorStyle = SECTOR_COLORS[sector] || SECTOR_COLORS.Other;
  const isInactive = status && status !== 'Actief';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        bgcolor: T.surface, borderRadius: '0.625rem',
        px: 2, py: 1.5,
        cursor: 'pointer',
        border: '1px solid rgba(45,51,53,0.06)',
        transition: 'all 0.15s',
        opacity: isInactive ? 0.55 : 1,
        '&:hover': {
          borderColor: `${T.primary}25`,
          boxShadow: '0 4px 16px rgba(45,51,53,0.06)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <LogoMetTerugval src={logoUrl}
        sx={{
          width: 36, height: 36, borderRadius: '0.5rem', objectFit: 'contain',
          bgcolor: T.surfaceLow, p: 0.25, flexShrink: 0,
        }}
        fallback={(
        <Box sx={{
          width: 36, height: 36, borderRadius: '0.5rem',
          bgcolor: sectorStyle.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: sectorStyle.color, lineHeight: 1 }}>
            {naam.substring(0, 2).toUpperCase()}
          </Typography>
        </Box>
        )}
      />

      <Typography noWrap sx={{
        fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 600, color: T.text,
        flex: 1, minWidth: 0,
      }}>
        {naam}
      </Typography>

      {sector && (
        <Chip label={sector} size="small"
          sx={{
            fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, height: 20,
            color: sectorStyle.color, bgcolor: sectorStyle.bg,
            textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0,
          }}
        />
      )}

      {isInactive && (
        <Chip label={status} size="small"
          sx={{
            fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, height: 20,
            color: '#7c3aed', bgcolor: '#ede9fe', flexShrink: 0,
          }}
        />
      )}

      {aantalProjecten > 0 && (
        <Tooltip
          arrow
          placement="bottom"
          title={`${aantalProjecten} ${aantalProjecten === 1 ? 'project' : 'projecten'}`}
          componentsProps={{
            tooltip: { sx: { bgcolor: '#2d3335', borderRadius: '0.375rem', fontSize: '0.72rem', px: 1.25, py: 0.5 } },
            arrow: { sx: { color: '#2d3335' } },
          }}
        >
          <Chip
            label={`${aantalProjecten}`}
            size="small"
            icon={<Box component="span" className="material-symbols-outlined" sx={{ fontSize: '13px !important', color: `${T.outline} !important` }}>folder</Box>}
            sx={{
              fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, height: 20,
              color: T.outline, bgcolor: T.surfaceLow, flexShrink: 0,
              '& .MuiChip-icon': { ml: 0.75, mr: -0.5 },
            }}
          />
        </Tooltip>
      )}

      {bedrijvenNamen.length > 0 && (
        <Tooltip
          arrow
          placement="bottom-start"
          title={
            <Box sx={{ py: 0.25 }}>
              {bedrijvenNamen.map((b, i) => (
                <Typography key={i} sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.6 }}>
                  {b}
                </Typography>
              ))}
            </Box>
          }
          componentsProps={{
            tooltip: { sx: { bgcolor: '#2d3335', borderRadius: '0.5rem', px: 1.5, py: 0.75, maxWidth: 280 } },
            arrow: { sx: { color: '#2d3335' } },
          }}
        >
          <Chip
            label={`${bedrijvenNamen.length}`}
            size="small"
            icon={<Box component="span" className="material-symbols-outlined" sx={{ fontSize: '13px !important', color: `${T.outline} !important` }}>domain</Box>}
            sx={{
              fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, height: 20,
              color: T.outline, bgcolor: T.surfaceLow, flexShrink: 0,
              '& .MuiChip-icon': { ml: 0.75, mr: -0.5 },
            }}
          />
        </Tooltip>
      )}

      <Icon name="chevron_right" size={18} sx={{ color: T.outlineVariant, flexShrink: 0 }} />
    </Box>
  );
}

export default function AccountsHub() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [klanten, setKlanten] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('az');
  // Inactieve accounts standaard verbergen (Joris, 6 sep); keuze onthouden per browser.
  const [toonInactief, setToonInactief] = useState(() => { try { return localStorage.getItem('accounts-toon-inactief') === '1'; } catch { return false; } });
  // 'Mijn accounts': alleen de accounts waar ik accountmanager van ben (wiki Account, 9 sep).
  // De accountmanager staat in de CRM-instellingen per account (Relatiebeheer-blok).
  const [alleenMijn, setAlleenMijn] = useState(() => { try { return localStorage.getItem('accounts-alleen-mijn') === '1'; } catch { return false; } });
  const zetAlleenMijn = (v) => { setAlleenMijn(v); try { localStorage.setItem('accounts-alleen-mijn', v ? '1' : '0'); } catch { /* prive-modus */ } };
  const [accountmanagerPerAccount, setAccountmanagerPerAccount] = useState({});
  useEffect(() => {
    authFetch(`${BACKEND_URL}/Api/klanten-crm/overview`).then((r) => (r.ok ? r.json() : { accounts: [] })).then((d) => {
      const map = {};
      (d.accounts || []).forEach((a) => {
        const id = a.account_id || a.account_airtable_id || a.accountId || a.id;
        const am = a.accountmanager || a.settings?.accountmanager || a.verantwoordelijk?.bel || '';
        if (id && am) map[id] = String(am);
      });
      setAccountmanagerPerAccount(map);
    }).catch(() => setAccountmanagerPerAccount({}));
  }, []);
  const { accounts: msalAccounts } = useMsal();
  const mijnNaam = String(msalAccounts?.[0]?.name || '').trim().toLowerCase();
  const isVanMij = (k) => {
    // Twee bronnen: de CRM-instelling, en anders de accountmanager die al op het
    // account zelf staat. Alleen de eerste gebruiken liet accounts buiten beeld
    // die nooit een CRM-record kregen (Codex, 11 sep).
    // Namen eerst: `Accountmanager` is de Airtable-koppeling en bevat record-id's
    // (recXXXX) — daar matcht nooit een naam op (audit 19 sep: Ton zag 0 accounts).
    const rauw = k.fields?.Accountmanager;
    const opAccount = k.fields?.AccountmanagerNamen
      || (Array.isArray(rauw) ? rauw.filter((x) => !/^rec[A-Za-z0-9]{14}$/.test(String(x))).join(', ') : (rauw || ''));
    const am = String(accountmanagerPerAccount[k.id] || opAccount || '').toLowerCase();
    if (!am || !mijnNaam) return false;
    const voornaam = mijnNaam.split(' ')[0];
    return am.includes(mijnNaam) || (voornaam.length > 2 && am.split(/[ ,]+/).includes(voornaam));
  };
  const aantalVanMij = useMemo(() => klanten.filter(isVanMij).length, [klanten, accountmanagerPerAccount, mijnNaam]); // eslint-disable-line react-hooks/exhaustive-deps
  const zetToonInactief = (v) => { setToonInactief(v); try { localStorage.setItem('accounts-toon-inactief', v ? '1' : '0'); } catch { /* privémodus */ } };
  const [linkedIds, setLinkedIds] = useState([]);
  const [unlinkedOpen, setUnlinkedOpen] = useState(false);
  const [unlinkedBedrijven, setUnlinkedBedrijven] = useState(null);
  // Nieuw account (8 okt): zelfde recht als accountbeheer in de Sales-hoek.
  const { permissions } = useUserRole() || {};
  const magAanmaken = !!permissions?.sales_dashboard?.edit;
  const [nieuwOpen, setNieuwOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(`${BACKEND_URL}/Api/klanten`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setKlanten(data.klanten || []);
        setLinkedIds(data.linkedBedrijvenIds || []);
      } catch (err) {
        setError(`Kon accounts niet laden: ${err.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Groepen per persoon: naam → accounts. Wie nergens op staat valt onder
  // 'Nog niemand', want dat is precies wat je wilt zien in zo'n overzicht.
  const aantalInactief = useMemo(() => klanten.filter(k => k.fields?.Status && k.fields.Status !== 'Actief').length, [klanten]);

  const filteredKlanten = useMemo(() => {
    // Zonder zoekterm blijven inactieve accounts weg tenzij de schakelaar aan staat;
    // mét zoekterm tonen we alles, anders vind je een oud account nooit terug.
    let basis = (searchTerm || toonInactief) ? klanten : klanten.filter(k => !(k.fields?.Status && k.fields.Status !== 'Actief'));
    if (alleenMijn) basis = basis.filter(isVanMij);
    if (!searchTerm) return basis;
    const term = searchTerm.toLowerCase();
    return basis.filter(k => {
      const naam = (k.fields?.Account || '').toLowerCase();
      const sector = (k.fields?.Sector || '').toLowerCase();
      const bedrijven = (k.fields?.BedrijvenNamen || []).join(' ').toLowerCase();
      return naam.includes(term) || sector.includes(term) || bedrijven.includes(term);
    });
  }, [klanten, searchTerm, toonInactief, alleenMijn, accountmanagerPerAccount, mijnNaam]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedKlanten = useMemo(() => {
    return [...filteredKlanten].sort((a, b) => {
      // Inactive always last
      const statusA = a.fields?.Status === 'Actief' ? 0 : 1;
      const statusB = b.fields?.Status === 'Actief' ? 0 : 1;
      if (statusA !== statusB) return statusA - statusB;

      if (sortBy === 'sector') {
        const sa = a.fields?.Sector || 'zzz';
        const sb = b.fields?.Sector || 'zzz';
        const cmp = sa.localeCompare(sb);
        if (cmp !== 0) return cmp;
        return (a.fields?.Account || '').localeCompare(b.fields?.Account || '');
      }
      if (sortBy === 'projecten') {
        const pa = a.fields?.['Aantal projecten Rollup (from Bedrijven)'] || a.fields?.['Aantal projecten'] || 0;
        const pb = b.fields?.['Aantal projecten Rollup (from Bedrijven)'] || b.fields?.['Aantal projecten'] || 0;
        return pb - pa;
      }
      if (sortBy === 'budget') return (b.fields?.Budget2026 || 0) - (a.fields?.Budget2026 || 0);
      return (a.fields?.Account || '').localeCompare(b.fields?.Account || '');
    });
  }, [filteredKlanten, sortBy]);

  // Ná sortedKlanten: stond hij ervóór, dan las de deps-array een const vóór zijn declaratie
  // (ReferenceError 'Cannot access before initialization' op /accounts, 15 sep).
  const perPersoon = useMemo(() => {
    const veld = sortBy === 'am' ? 'AccountmanagerNamen' : 'ProjectleidersNamen';
    const kaart = new Map();
    (sortedKlanten || []).forEach((k) => {
      const ruw = k.fields?.[veld];
      const namen = (Array.isArray(ruw) ? ruw.join(', ') : String(ruw || ''))
        .split(',').map((n) => n.trim()).filter(Boolean);
      (namen.length ? namen : ['Nog niemand']).forEach((naam) => {
        if (!kaart.has(naam)) kaart.set(naam, []);
        kaart.get(naam).push(k);
      });
    });
    return [...kaart.entries()].sort((a, b) => {
      if (a[0] === 'Nog niemand') return 1;
      if (b[0] === 'Nog niemand') return -1;
      return b[1].length - a[1].length || a[0].localeCompare(b[0]);
    });
  }, [sortedKlanten, sortBy]);


  const grouped = useMemo(() => {
    const map = {};
    if (sortBy === 'sector') {
      sortedKlanten.forEach(k => {
        const sector = k.fields?.Sector || 'Overig';
        if (!map[sector]) map[sector] = [];
        map[sector].push(k);
      });
      return Object.entries(map);
    }
    sortedKlanten.forEach(k => {
      const letter = (k.fields?.Account || '?')[0].toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(k);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [sortedKlanten, sortBy]);

  const openUnlinked = async () => {
    setUnlinkedOpen(true);
    if (unlinkedBedrijven !== null) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/unlinked-bedrijven?linkedIds=${linkedIds.join(',')}`);
      if (res.ok) {
        const data = await res.json();
        setUnlinkedBedrijven(data.bedrijven || []);
      }
    } catch { setUnlinkedBedrijven([]); }
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      <PageHeader
        icon="business"
        title="Accounts"
        subtitle={loading ? 'Laden...' : `${sortedKlanten.length} accounts`}
        rightActions={magAanmaken ? (
          <Button variant="contained" size="small" onClick={() => setNieuwOpen(true)}
            startIcon={<Icon name="add" size={18} />} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Nieuw account
          </Button>
        ) : null}
      />
      {magAanmaken && (
        <NieuwAccountDialog
          open={nieuwOpen}
          onClose={() => setNieuwOpen(false)}
          onAangemaakt={(d) => { setNieuwOpen(false); navigate(`/accounts/${d.id}/workspace`); }}
          onOngedaan={(id) => { if (window.location.pathname.includes(id)) navigate('/accounts', { replace: true }); }}
        />
      )}

      {/* Search + Sort */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Zoek op account, bedrijf, sector..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.outlineVariant }} /></InputAdornment>,
          }}
          sx={{
            flex: 1, maxWidth: 480, minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: '9999px', fontSize: '0.84rem', bgcolor: T.surface,
              '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[
            { id: 'az', label: 'A–Z' },
            { id: 'sector', label: 'Sector' },
            { id: 'projecten', label: 'Projecten' },
            { id: 'budget', label: 'Budget' },
            // Wie doet wat (Joris, 11 sep): dezelfde kaarten, maar gegroepeerd
            // per persoon. Een account met twee managers staat onder allebei.
            { id: 'am', label: 'Per accountmanager' },
            { id: 'pl', label: 'Per PL' },
          ].map(s => (
            <Chip
              key={s.id}
              label={s.label}
              size="small"
              onClick={() => setSortBy(s.id)}
              sx={{
                fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, height: 28,
                cursor: 'pointer',
                bgcolor: sortBy === s.id ? T.primaryContainer : T.surfaceLow,
                color: sortBy === s.id ? T.primary : T.outline,
                '&:hover': { bgcolor: sortBy === s.id ? T.primaryContainer : '#e5e9eb' },
              }}
            />
          ))}
          <Tooltip title={alleenMijn ? 'Alle accounts tonen' : 'Alleen de accounts waar ik accountmanager van ben (in te stellen bij Relatiebeheer op de accountpagina)'}>
            <Chip size="small" clickable label={alleenMijn ? `Mijn accounts · ${aantalVanMij}` : `Mijn accounts · ${aantalVanMij}`}
              color={alleenMijn ? 'primary' : 'default'} variant={alleenMijn ? 'filled' : 'outlined'}
              onClick={() => zetAlleenMijn(!alleenMijn)} sx={{ fontWeight: 700, mr: 0.75 }} />
          </Tooltip>
          {aantalInactief > 0 && (
            <Tooltip title={toonInactief ? 'Inactieve accounts verbergen' : `${aantalInactief} inactieve accounts tonen`}>
              <Chip
                label={toonInactief ? `Inactief aan · ${aantalInactief}` : `Inactief uit · ${aantalInactief}`}
                size="small"
                onClick={() => zetToonInactief(!toonInactief)}
                sx={{
                  fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, height: 28, cursor: 'pointer', ml: 1,
                  bgcolor: toonInactief ? T.primaryContainer : T.surfaceLow,
                  color: toonInactief ? T.primary : T.outline,
                  '&:hover': { bgcolor: toonInactief ? T.primaryContainer : '#e5e9eb' },
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Unlinked bedrijven collectie */}
      {!loading && linkedIds.length > 0 && (
        <Box
          onClick={openUnlinked}
          sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            mb: 3, px: 2.5, py: 1.5, borderRadius: '0.625rem',
            bgcolor: T.surfaceLow, border: '1px dashed rgba(45,51,53,0.12)',
            cursor: 'pointer', transition: 'all 0.15s',
            '&:hover': { borderColor: `${T.primary}30`, bgcolor: T.surface },
          }}
        >
          <Icon name="folder_special" size={20} sx={{ color: T.outlineVariant }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.outline }}>
            Bedrijven zonder account
          </Typography>
          <Icon name="chevron_right" size={18} sx={{ color: T.outlineVariant }} />
        </Box>
      )}

      {/* Unlinked dialog */}
      <Dialog
        open={unlinkedOpen}
        onClose={() => setUnlinkedOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '0.75rem', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>
              Bedrijven zonder account
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>
              Bedrijven met projecten die niet aan een account zijn gekoppeld
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setUnlinkedOpen(false)} sx={{ color: T.outlineVariant }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          {unlinkedBedrijven === null ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>Laden...</Typography>
            </Box>
          ) : unlinkedBedrijven.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>Alle bedrijven zijn aan een account gekoppeld</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {unlinkedBedrijven.map(b => (
                <Box key={b.id} sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  px: 2, py: 1.25, borderRadius: '0.5rem',
                  border: '1px solid rgba(45,51,53,0.06)',
                  '&:hover': { bgcolor: T.surfaceLow },
                }}>
                  <LogoMetTerugval src={b.logoUrl} sx={{
                    width: 28, height: 28, borderRadius: '0.375rem', objectFit: 'contain',
                    bgcolor: T.surfaceLow, p: 0.25, flexShrink: 0,
                  }} fallback={(
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '0.375rem',
                      bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: '0.52rem', fontWeight: 800, color: T.outline }}>
                        {(b.name || '?').substring(0, 2).toUpperCase()}
                      </Typography>
                    </Box>
                  )} />
                  <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 600, color: T.text, flex: 1, minWidth: 0 }}>
                    {b.name}
                  </Typography>
                  <Chip
                    label={`${b.projectCount}`}
                    size="small"
                    icon={<Box component="span" className="material-symbols-outlined" sx={{ fontSize: '13px !important', color: `${T.outline} !important` }}>folder</Box>}
                    sx={{
                      fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, height: 20,
                      color: T.outline, bgcolor: T.surfaceLow, flexShrink: 0,
                      '& .MuiChip-icon': { ml: 0.75, mr: -0.5 },
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Error */}
      {error && (
        <Box sx={{ p: 2.5, mb: 4, borderRadius: '0.75rem', bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: '#991b1b' }}>{error}</Typography>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: '0.625rem' }} />
          ))}
        </Box>
      )}

      {/* Empty */}
      {!loading && !error && sortedKlanten.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Icon name="business" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 16px' }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', color: T.outline }}>
            {searchTerm ? 'Geen accounts gevonden' : 'Geen accounts beschikbaar'}
          </Typography>
        </Box>
      )}

      {/* List */}
      {!loading && sortedKlanten.length > 0 && (sortBy === 'az' || sortBy === 'sector') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {grouped.map(([letter, items]) => (
            <Box key={letter}>
              <Typography sx={{
                fontFamily: T.font, fontSize: '0.72rem', fontWeight: 800, color: T.outlineVariant,
                textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1, ml: 0.5,
              }}>
                {letter}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                {items.map(klant => (
                  <AccountCard key={klant.id} klant={klant} onClick={() => navigate(`/accounts/${klant.id}/workspace`)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {!loading && sortedKlanten.length > 0 && (sortBy === 'am' || sortBy === 'pl') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {perPersoon.map(([naam, items]) => (
            <Box key={naam}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, ml: 0.5 }}>
                <Typography sx={{
                  fontFamily: T.font, fontSize: '0.82rem', fontWeight: 800,
                  color: naam === 'Nog niemand' ? T.outlineVariant : T.text,
                }}>
                  {naam}
                </Typography>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant }}>
                  {items.length} account{items.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                {items.map(klant => (
                  <AccountCard key={`${naam}-${klant.id}`} klant={klant}
                    onClick={() => navigate(`/accounts/${klant.id}/workspace`)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {!loading && sortedKlanten.length > 0 && !['az', 'sector', 'am', 'pl'].includes(sortBy) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
          {sortedKlanten.map(klant => (
            <AccountCard key={klant.id} klant={klant} onClick={() => navigate(`/accounts/${klant.id}/workspace`)} />
          ))}
        </Box>
      )}
    </Box>
  );
}
